<!--
	The header of this build carries the section switcher and nothing else of its own. The pages
	navigation — Projects and the rest — is NOT projected here: it moves down a level with the
	sections, and the header's `[headerNav]` slot stays empty rather than being filled with a copy
	that would have to be removed again. `ds-navigation` is therefore absent from this build's graph
	entirely, the same way the rail is absent from the other one.
-->
<!--
	The title names the open project (or the Platform Administrator group) in the band's centre
	(EFF-26168): with the Domains & Projects menu in the side panel, the project subheader no longer
	carries it. The other build keeps it there and projects no title.
-->
<ds-header class="ds-platform-shell__header">
	<eff-app-section-switcher headerSections />
	<ds-header-title headerTitle vertical="center" class="ds-platform-shell__header-title" [title]="headerTitle()" />
</ds-header>

<div class="ds-flex eff-flex--full ds-platform-shell__body">
	<!--
		The rail is a SIBLING of the shell below, not a child of its aside: the aside is a vertical
		stack with pinned header and footer slots, and the rail has to outlive it.
	-->
	<!--
		The user menu is COMPOSED into the rail's footer slot, not configured through the rail: the rail
		takes no inputs by contract, and the menu reads the session, the permission ladder and the
		billing config — none of which a `type:ui` library may know about (EFF-26081).
	-->
	<eff-app-rail class="ds-platform-shell__rail">
		<ds-user-menu effAppRailFooter />
	</eff-app-rail>

	<!--
		Overlay mode, and it sits BEFORE the shell on purpose: the block is absolutely positioned, so DOM
		order is free to match the visual order the user perceives — panel first, then the content it
		covers. It also carries the landmark and the name the kit's aside contributes in push mode, so the
		panel is the same region to assistive technology in both modes rather than an anonymous div in one
		of them.
	-->
	@if (isPanelOpen() && panelRenderMode() === APP_SHELL_PANEL_MODE.Overlay) {
		<div
			class="ds-platform-shell__overlay-panel"
			role="complementary"
			aria-label="Section panel"
			[class.ds-platform-shell__overlay-panel--navigation]="isNavigationPanelOpen()"
			[style.--ds-platform-shell-panel-width]="panelWidth()"
		>
			<!--
				The same band half push mode projects into the kit's pinned aside header slot. It is here
				because the control that closes the panel and the name of what is open are the panel's own
				chrome, not the aside's: a panel that floats over the page needs them more than one that
				sits beside it, having taken the page's own space to draw on. The navigation tree is the
				exception: its own menu header carries the first line, and the rail button closes it.
			-->
			@if (!isNavigationPanelOpen()) {
				<eff-app-panel-header class="slds-shrink-none" />
			}

			<div class="ds-platform-shell__overlay-body">
				<eff-app-side-panel />
			</div>
		</div>
	}

	<eff-sidebar-shell
		ariaLabel="Section panel"
		class="eff-flex--full ds-platform-shell__content"
		[asideWidth]="panelWidth()"
		[collapsible]="false"
	>
		<!--
			Closed is the ABSENCE of the aside rather than a narrowed one — which is also what keeps
			a closed panel out of the accessibility tree with no `inert` attribute. Push mode is the
			only mode that ever projects the aside; overlay mode leaves it unprojected and renders the
			panel through the sibling container below instead. The reasoning in full is on the
			component class.
		-->
		@if (isPanelOpen() && panelRenderMode() === APP_SHELL_PANEL_MODE.Push) {
			<!--
				No divider against main: the kit draws it on the aside HOST, so it would run the
				pane's full height and cut through the chrome band that spans both columns. The
				rail already carries the only vertical edge this region needs, on its far side.
			-->
			<eff-sidebar-shell-aside class="ds-platform-shell__aside" [divider]="false">
				<!--
					The aside half of the chrome band. In the kit's PINNED top slot, so it stays put
					while the panel body scrolls under it — and so it lines up with the tab strip
					across the column boundary.
				-->
				<eff-sidebar-shell-aside-header>
					<eff-app-panel-header />
				</eff-sidebar-shell-aside-header>

				<eff-app-side-panel />
			</eff-sidebar-shell-aside>
		}

		<eff-sidebar-shell-main class="ds-platform-shell__main">
			<!--
				The main half of the chrome band, and the reason it is HERE rather than above the
				body row: the tabs belong over main, so opening and closing the panel must not slide
				them sideways. Main is the page scroll-host, so the strip is pinned to its top edge
				from the stylesheet — without that it would scroll away with the page, which it never
				did while it lived above this row.
			-->
			<eff-app-tab-strip class="slds-shrink-none ds-platform-shell__tabs" />

			<!--
				The Platform Administrator row (EFF-24841), which used to render from `PagesComponent`.
				It is section chrome, so it belongs to the shell that has sections: the build without
				them reaches those four pages from the header navigation, and a second row of the same
				entries under the header is a duplicate. Still inside main and still in the content
				column, which is where the design puts it — only the file it is written in changed.
				It is empty, and so renders nothing at all, on every URL outside that group.
			-->
			<eff-app-tab-strip
				class="slds-shrink-none ds-platform-shell__platform-admin-tabs"
				[tabs]="platformAdminTabs()"
			/>

			<router-outlet />
		</eff-sidebar-shell-main>
	</eff-sidebar-shell>
</div>
